'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getAppVersionCode, getOsType } from '@/lib/CookieUtils';
import { isAppClient } from '@/lib/LocalStorage';
import { appRPC } from '@/lib/utils';
import { Product } from '@/models/Product';
import { useToast } from '../ui/use-toast';
import {
  PhotoCameraCapture,
  PhotoCropEditor,
  PhotoCustomizePrompt,
  PhotoFilterEditor,
  PhotoPreview,
  PhotoSourceSelector,
} from './PhotoPrintFlowScreens';
import {
  type PhotoPrintButtonEvent,
  type PhotoPrintDraft,
  type PhotoPrintStep,
} from './PhotoPrintFlowTypes';
import { createInitialDraft, stopPhotoPrintButtonEvent } from './PhotoPrintFlowUtils';
import {
  buildPhotoPrintEditorFile,
  getPhotoPrintCaptionStyle,
} from './PhotoPrintImageExport';

export interface PhotoPrintEditorResult {
  file: File;
  dataUrl: string;
  caption: string;
  captionStyle: string;
  quantity: number;
  sendWithoutText: boolean;
}

export interface PhotoPrintEditorProps {
  selectedProduct: Product;
  currentImageUrl?: string | null;
  showCaptionDialog?: boolean;
  quantity?: number;
  confirmLabel?: string;
  onCancel: () => void;
  onComplete: (result: PhotoPrintEditorResult) => void | Promise<void>;
  isSubmitting?: boolean;
}

function normalizePhotoPrintQuantity(quantity: unknown) {
  const numericQuantity = Number(quantity);
  return Number.isFinite(numericQuantity) ? Math.max(1, numericQuantity) : 1;
}

function normalizeNativePhotoPrintImage(imageValue: unknown, mimeType?: unknown) {
  if (typeof imageValue !== 'string') return null;

  const rawImage = imageValue.trim();
  if (!rawImage) return null;
  if (rawImage.startsWith('data:image/')) return rawImage;

  const safeMimeType =
    typeof mimeType === 'string' && mimeType.startsWith('image/') ? mimeType : 'image/jpeg';

  return `data:${safeMimeType};base64,${rawImage}`;
}

function getNativePhotoPrintImageData(message: unknown) {
  let payload = message;

  if (typeof message === 'string') {
    try {
      payload = JSON.parse(message) as Record<string, unknown>;
    } catch {
      payload = { imageBase64: message };
    }
  }

  if (!payload || typeof payload !== 'object') return null;

  const data = payload as Record<string, unknown>;
  const imageValue = data.imageBase64 ?? data.image ?? data.photoBase64 ?? data.dataUrl;

  return normalizeNativePhotoPrintImage(imageValue, data.mimeType);
}

function getPhotoPrintImageFromEventDetail(detail: Record<string, unknown>) {
  const fromMessage = getNativePhotoPrintImageData(detail.message);
  if (fromMessage) return fromMessage;

  return getNativePhotoPrintImageData(detail);
}

export default function PhotoPrintEditor({
  selectedProduct,
  currentImageUrl = null,
  showCaptionDialog,
  quantity = 1,
  confirmLabel,
  onCancel,
  onComplete,
  isSubmitting = false,
}: PhotoPrintEditorProps) {
  const { toast } = useToast();
  const [draft, setDraft] = useState<PhotoPrintDraft>(() => ({
    ...createInitialDraft(),
    selectedProduct,
    step: 'source',
    quantity: normalizePhotoPrintQuantity(quantity),
    sendWithoutText: showCaptionDialog === false,
  }));
  const [pageIsApp, setPageIsApp] = useState(false);
  const [localSubmitting, setLocalSubmitting] = useState(false);
  const [openCropOverlayOnEnter, setOpenCropOverlayOnEnter] = useState(false);
  const pendingNativeSourceRef = useRef<'camera' | 'gallery'>('gallery');

  const captionEnabled = showCaptionDialog ?? selectedProduct.showCaptionDialog !== false;
  const submitting = isSubmitting || localSubmitting;

  const isPhotoPrintSupported = useMemo(() => {
    if (!pageIsApp) return false;

    const osType = getOsType();
    const versionStr = getAppVersionCode();
    const versionCode = versionStr ? Number(versionStr) : NaN;

    if (!osType || Number.isNaN(versionCode)) return false;

    if (osType === 'android') return versionCode >= 10116;
    if (osType === 'ios') return versionCode >= 10115;

    return false;
  }, [pageIsApp]);

  useEffect(() => {
    setPageIsApp(isAppClient());
  }, []);

  useEffect(() => {
    setDraft((prev) => ({
      ...prev,
      selectedProduct,
      quantity: normalizePhotoPrintQuantity(quantity),
      sendWithoutText: showCaptionDialog === false ? true : prev.sendWithoutText,
    }));
  }, [quantity, selectedProduct, showCaptionDialog]);

  useEffect(() => {
    if (!isPhotoPrintSupported) return;

    const handler = (event: Event) => {
      const customEvent = event as CustomEvent;
      const detail = customEvent.detail as { status?: string; message?: unknown } | undefined;

      if (!detail?.status) return;

      if (detail.status === 'error') {
        toast({
          variant: 'destructive',
          description:
            typeof detail.message === 'string'
              ? detail.message
              : 'Fotoğraf işlenirken hata oluştu.',
        });
        return;
      }

      if (detail.status === 'cancelled') {
        return;
      }

      if (detail.status !== 'photo_ready') {
        return;
      }

      const imageDataUrl = getPhotoPrintImageFromEventDetail(
        detail as Record<string, unknown>,
      );

      if (!imageDataUrl) {
        toast({
          variant: 'destructive',
          description: 'Fotoğraf okunamadı. Lütfen tekrar deneyin.',
        });
        return;
      }

      setDraft((prev) => ({
        ...prev,
        source: pendingNativeSourceRef.current,
        originalImage: imageDataUrl,
        editedImage: imageDataUrl,
        caption: prev.caption || '',
        step: 'customizePrompt',
      }));
    };

    window.addEventListener('hzc-photo-print', handler as EventListener);
    return () => window.removeEventListener('hzc-photo-print', handler as EventListener);
  }, [isPhotoPrintSupported, toast]);

  const imageForEditing = draft.editedImage || draft.originalImage;

  const setStep = useCallback((step: PhotoPrintStep) => {
    setDraft((prev) => ({
      ...prev,
      step,
    }));
  }, []);

  const updateDraft = useCallback((partial: Partial<PhotoPrintDraft>) => {
    setDraft((prev) => ({
      ...prev,
      ...partial,
    }));
  }, []);

  const safePhotoPrintAppRPC = useCallback(
    (method: 'openGalleryForPhotoPrint') => {
      try {
        const RPCcheck = isPhotoPrintSupported ? appRPC('bridge', method, null) : false;
        if (!RPCcheck) {
          toast({
            variant: 'destructive',
            description: 'Bu özellik şu anda kullanılamıyor.',
          });
        }
      } catch {
        toast({
          variant: 'destructive',
          description: 'Bu özellik şu anda kullanılamıyor.',
        });
      }
    },
    [isPhotoPrintSupported, toast],
  );

  const handleOpenCamera = useCallback((event?: PhotoPrintButtonEvent) => {
    event?.preventDefault();
    event?.stopPropagation();
    setDraft((prev) => ({
      ...prev,
      source: 'camera',
      step: 'camera',
    }));
  }, []);

  const handleCapturePhoto = useCallback((imageDataUrl: string) => {
    setDraft((prev) => ({
      ...prev,
      originalImage: imageDataUrl,
      editedImage: imageDataUrl,
      caption: prev.caption || '',
      step: 'customizePrompt',
    }));
  }, []);

  const handleSelectGalleryImage = useCallback((imageDataUrl: string) => {
    setDraft((prev) => ({
      ...prev,
      source: 'gallery',
      originalImage: imageDataUrl,
      editedImage: imageDataUrl,
      caption: prev.caption || '',
      step: 'customizePrompt',
    }));
  }, []);

  const handleOpenGallery = useCallback(
    (event?: PhotoPrintButtonEvent) => {
      event?.preventDefault();
      event?.stopPropagation();

      if (!isPhotoPrintSupported) return;

      pendingNativeSourceRef.current = 'gallery';
      safePhotoPrintAppRPC('openGalleryForPhotoPrint');
    },
    [isPhotoPrintSupported, safePhotoPrintAppRPC],
  );

  const handleSkipCustomize = useCallback((event: PhotoPrintButtonEvent) => {
    stopPhotoPrintButtonEvent(event);
    setDraft((prev) => ({
      ...prev,
      editedImage: prev.editedImage || prev.originalImage,
      step: 'preview',
    }));
  }, []);

  const handleStartCustomize = useCallback(
    (event: PhotoPrintButtonEvent) => {
      stopPhotoPrintButtonEvent(event);
      setOpenCropOverlayOnEnter(false);
      setStep('crop');
    },
    [setStep],
  );

  const handleCropFromAspectWarning = useCallback(
    (event: PhotoPrintButtonEvent) => {
      stopPhotoPrintButtonEvent(event);
      setOpenCropOverlayOnEnter(true);
      setStep('crop');
    },
    [setStep],
  );

  const handleDeleteImage = useCallback(() => {
    setDraft((prev) => ({
      ...prev,
      originalImage: null,
      editedImage: null,
      step: 'source',
    }));
  }, []);

  const handleCancel = useCallback(
    (event?: PhotoPrintButtonEvent) => {
      event?.preventDefault();
      event?.stopPropagation();
      onCancel();
    },
    [onCancel],
  );

  const handleConfirm = useCallback(
    async (event: PhotoPrintButtonEvent) => {
      stopPhotoPrintButtonEvent(event);
      if (submitting) return;

      setLocalSubmitting(true);
      try {
        const { file, dataUrl } = await buildPhotoPrintEditorFile(draft);
        if (!file || !dataUrl) {
          throw new Error('Lütfen bir fotoğraf seçin');
        }

        await onComplete({
          file,
          dataUrl,
          caption: !captionEnabled || draft.sendWithoutText ? '' : draft.caption,
          captionStyle: getPhotoPrintCaptionStyle(draft.textStyle),
          quantity: normalizePhotoPrintQuantity(draft.quantity),
          sendWithoutText: draft.sendWithoutText,
        });
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Fotoğraf hazırlanamadı';
        toast({
          variant: 'destructive',
          description: message,
        });
      } finally {
        setLocalSubmitting(false);
      }
    },
    [captionEnabled, draft, onComplete, submitting, toast],
  );

  if (draft.step === 'camera') {
    return (
      <PhotoCameraCapture
        selectedProduct={selectedProduct}
        onBack={() => setStep('source')}
        onCapture={handleCapturePhoto}
      />
    );
  }

  if (draft.step === 'customizePrompt' && imageForEditing) {
    return (
      <PhotoCustomizePrompt
        selectedProduct={selectedProduct}
        imageUrl={imageForEditing}
        onBack={() => setStep('source')}
        onCustomize={handleStartCustomize}
        onCropNow={handleCropFromAspectWarning}
        onSkip={handleSkipCustomize}
      />
    );
  }

  if (draft.step === 'crop' && imageForEditing) {
    return (
      <PhotoCropEditor
        draft={draft}
        imageUrl={imageForEditing}
        selectedProduct={selectedProduct}
        onBack={() => {
          setOpenCropOverlayOnEnter(false);
          setStep('customizePrompt');
        }}
        onChange={updateDraft}
        onNext={() => {
          setOpenCropOverlayOnEnter(false);
          setStep('filter');
        }}
        onChangePhoto={handleDeleteImage}
        initialOpenCropOverlay={openCropOverlayOnEnter}
      />
    );
  }

  if (draft.step === 'filter' && imageForEditing) {
    return (
      <PhotoFilterEditor
        draft={draft}
        imageUrl={imageForEditing}
        selectedProduct={selectedProduct}
        onBack={() => setStep('crop')}
        onChange={updateDraft}
        onNext={() => setStep('preview')}
        onChangePhoto={handleDeleteImage}
        showCaptionDialog={captionEnabled}
      />
    );
  }

  if (draft.step === 'preview' && imageForEditing) {
    return (
      <PhotoPreview
        draft={draft}
        imageUrl={imageForEditing}
        selectedProduct={selectedProduct}
        onBack={() => setStep('filter')}
        onChange={updateDraft}
        onConfirm={handleConfirm}
        isSubmitting={submitting}
        onChangePhoto={handleDeleteImage}
        showCaptionDialog={captionEnabled}
        confirmLabel={confirmLabel}
      />
    );
  }

  return (
    <PhotoSourceSelector
      pageIsApp={isPhotoPrintSupported}
      selectedProduct={selectedProduct}
      currentImageUrl={currentImageUrl}
      onBack={handleCancel}
      onOpenCamera={handleOpenCamera}
      onOpenGallery={handleOpenGallery}
      onSelectGalleryImage={handleSelectGalleryImage}
    />
  );
}
